import { Grid, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VerticalAlignGrid } from "../../../invoices/styled";
import { CustomerContract } from "../../customerAPI";
import { updateContractPricing } from "../../customerContractsSlice";
import {
  buildSpecialPricingTableDataArray,
  buildTesPricingTableDataArray,
  buildUpdatedPricingObjectForApi,
  PricingGeneralSettings,
  validatePricingGeneralSettings,
} from "../helpers/pricingUtils";
import PricingEditModal from "./form/common/PricingEditModal";
import PricingTablesSection from "./form/common/PricingTablesSection";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import { formatDateForBE } from "../helpers/commonUtils";

interface PricingData {
  pricingValidFromDate: string;
  pricingGeneralSettings: PricingGeneralSettings;
  pricingTesList: object[];
}

interface Props {
  contractData: CustomerContract;
  pricingData: PricingData;
  eezyCompanyName: string;
  closeModal: () => void;
}

const EditExistingPricingForm = ({
  contractData,
  pricingData,
  eezyCompanyName,
  closeModal,
}: Props) => {
  const { t } = useTranslation("customers");
  const dispatch = useAppDispatch();
  const { tes } = useAppSelector((state) => state.localized);
  const { status } = useAppSelector((state) => state.customerContracts);
  const isLoading = status === "pending";
  const [modalState, setModalState] = useState<{
    show: boolean;
    selectedItemId?: string;
  }>({
    show: false,
    selectedItemId: "",
  });
  const isGeneralPricingModal = modalState.selectedItemId === "general";

  const defaultValues = {
    pricingValidFromDate: new Date(pricingData.pricingValidFromDate),
    pricingGeneralSettings: pricingData.pricingGeneralSettings,
    pricingTesList: pricingData.pricingTesList || [],
  };

  const methods = useForm({ defaultValues, mode: "onChange" });

  const {
    handleSubmit,
    reset,
    watch,
    control,
    formState: { isDirty, errors, isSubmitting },
  } = methods;

  const watchedPricingGeneralSettings = watch("pricingGeneralSettings");
  const watchedPricingTesList = watch("pricingTesList");

  const onSubmit = (formData: any) => {
    const formDate = formatDateForBE(formData.pricingValidFromDate);
    const isBackDate = formDate !== pricingData.pricingValidFromDate;
    const backDate = isBackDate ? formDate : undefined;
    const updatedPricing = buildUpdatedPricingObjectForApi({
      contractId: contractData.id,
      contractVersion: contractData.version,
      validFrom: pricingData.pricingValidFromDate,
      backDate,
      pricingGeneralSettings: formData.pricingGeneralSettings,
      pricingTesList: formData.pricingTesList,
    });

    dispatch(updateContractPricing(updatedPricing)).then((action) => {
      if (isFulfilled(action)) closeModal();
    });
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeModal();
  };

  const onOpenModal = (selectedItemId?: string) => {
    setModalState({ show: true, selectedItemId });
  };
  const onCloseModal = () => setModalState({ ...modalState, show: false });
  const onModalExited = () =>
    setModalState({ show: false, selectedItemId: "" });

  const tesPricingDataArray = buildTesPricingTableDataArray({
    pricingTesList: watchedPricingTesList,
    tesLocalizationIndex: tes,
  });

  const specialPricingDataArray = buildSpecialPricingTableDataArray({
    pricingGeneralSettings: watchedPricingGeneralSettings,
    label: t("contract.generalPricing"),
  });

  const getPricingPerTesData = () => {
    const foundData = watchedPricingTesList.find(
      (tesItem: any) => tesItem.tesId === modalState.selectedItemId,
    );
    return {
      pricingValidFromDate: pricingData.pricingValidFromDate,
      pricingTesItem: foundData || {},
    };
  };

  const getPricingGeneralSettingsData = () => {
    return {
      pricingValidFromDate: pricingData.pricingValidFromDate,
      pricingGeneralSettings: watchedPricingGeneralSettings,
    };
  };

  const areValuesValid = validatePricingGeneralSettings(
    watchedPricingGeneralSettings,
  );

  const saveDisabled =
    !isDirty ||
    !areValuesValid ||
    !isEmpty(errors) ||
    isSubmitting ||
    isLoading;

  return (
    <DialogContentWrapper
      title={t("contract.pricing")}
      margin
      onCancel={handleClose}
      onSave={onSave}
      saveDisabled={saveDisabled}
      childContainerSx={{ overflowY: "auto", overflowX: "hidden" }}
      customSaveButtonLabel={t("common:saveEdits")}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <OverlaySpinner isLoading={isLoading} />
          <VerticalAlignGrid container spacing={1}>
            <Grid
              container
              item
              xs={4}
              justifyContent="flex-end"
              paddingRight="0.5rem"
            >
              <Typography variant="secondary">
                {t("settings:effectiveFrom")}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="pricingValidFromDate"
                control={control}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    disableOpenPicker
                    value={field.value}
                    format="dd.MM.yyyy"
                    onChange={(newDate: Date | null) => field.onChange(newDate)}
                    slots={{ textField: TextFieldLight }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { input: { textAlign: "center" } },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </VerticalAlignGrid>
          <PricingTablesSection
            generalPricingDataArray={specialPricingDataArray}
            tesPricingDataArray={tesPricingDataArray}
            onOpenModal={onOpenModal}
          />
          <PricingEditModal
            isOpen={modalState.show}
            onClose={onCloseModal}
            openedFromPricingContent
            isGeneralPricingModal={isGeneralPricingModal}
            contractData={contractData}
            eezyCompanyName={eezyCompanyName}
            pricingPerTesData={getPricingPerTesData()}
            pricingGeneralSettingsData={getPricingGeneralSettingsData()}
            TransitionProps={{ onExited: onModalExited }}
          />
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default EditExistingPricingForm;
